interface RasterizedPage {
  dataUrl: string
  widthPt: number
  heightPt: number
}

/**
 * Prints a PDF in Chrome by rasterizing every page to an image via
 * pdfjs, then printing via an HTML document with dimension-grouped
 * @page rules. Pages sharing the same dimensions share a single named
 * page rule (or an unnamed rule for single-dimension docs), which
 * avoids Chrome's blank-page-insertion bug that occurs when every page
 * has its own named page and Chrome forces a page break at each
 * named page transition.
 */
export async function printNormalizedPdf(signedUrl: string): Promise<void> {
  const pages = await rasterizePages(signedUrl)
  await printHtml(buildPerPageHtml(pages))
}

function pageDimKey(widthPt: number, heightPt: number): string {
  return `${Math.round(widthPt)}_${Math.round(heightPt)}`
}

function buildPerPageHtml(
  pages: Array<{ dataUrl: string; widthPt: number; heightPt: number }>
): string {
  const uniqueDims = new Map<string, { widthPt: number; heightPt: number }>()
  for (const { widthPt, heightPt } of pages) {
    const key = pageDimKey(widthPt, heightPt)
    if (!uniqueDims.has(key)) {
      uniqueDims.set(key, { widthPt, heightPt })
    }
  }

  const singleDim = uniqueDims.size === 1

  const pageRules = singleDim
    ? `@page { size: ${pages[0].widthPt}pt ${pages[0].heightPt}pt; margin: 0; }`
    : [
        `@page { margin: 0; }`,
        ...Array.from(uniqueDims.entries()).map(
          ([key, { widthPt, heightPt }]) =>
            `@page dim${key} { size: ${widthPt}pt ${heightPt}pt; margin: 0; }`
        ),
      ].join('\n  ')

  const dimStyles = singleDim
    ? `.page { width: ${pages[0].widthPt}pt; height: ${pages[0].heightPt}pt; }`
    : Array.from(uniqueDims.entries())
        .map(
          ([key, { widthPt, heightPt }]) =>
            `.dim${key} { page: dim${key}; width: ${widthPt}pt; height: ${heightPt}pt; }`
        )
        .join('\n  ')

  const body = pages
    .map(({ dataUrl, widthPt, heightPt }) => {
      const cls = singleDim
        ? 'page'
        : `page dim${pageDimKey(widthPt, heightPt)}`
      return `<div class="${cls}"><img src="${dataUrl}"></div>`
    })
    .join('\n')

  return `<!DOCTYPE html>
<html>
<head>
<style>
  ${pageRules}
  ${dimStyles}
  body { margin: 0; padding: 0; }
  .page { break-after: page; break-inside: avoid; overflow: hidden; contain: size layout; }
  .page:last-child { break-after: auto; }
  .page img { display: block; width: 100%; height: 100%; }
</style>
</head>
<body>
${body}
</body>
</html>`
}

/**
 * Bypasses CSS paged media entirely by rasterizing each page, embedding
 * the images into a new PDF via pdf-lib with correct per-page dimensions,
 * and opening that PDF in Chrome's native viewer. Intended as a fallback
 * if buildPerPageHtml still produces blank pages at orientation transitions
 * in mixed-orientation documents.
 */
export async function printReconstructedPdf(signedUrl: string): Promise<void> {
  const { PDFDocument } = await import('pdf-lib')
  const pages = await rasterizePages(signedUrl)

  const pdfDoc = await PDFDocument.create()

  for (const { dataUrl, widthPt, heightPt } of pages) {
    const pngImage = await pdfDoc.embedPng(dataUrlToUint8Array(dataUrl))
    const page = pdfDoc.addPage([widthPt, heightPt])
    page.drawImage(pngImage, {
      x: 0,
      y: 0,
      width: widthPt,
      height: heightPt,
    })
  }

  const pdfBytes = await pdfDoc.save()
  const arrayBuffer = new ArrayBuffer(pdfBytes.byteLength)
  new Uint8Array(arrayBuffer).set(pdfBytes)
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' })
  const blobUrl = URL.createObjectURL(blob)
  await printViaNewWindow(blobUrl)
}

function dataUrlToUint8Array(dataUrl: string): Uint8Array {
  const base64 = dataUrl.split(',')[1]
  if (!base64) {
    throw new Error('Invalid data URL: missing base64 content')
  }
  const binaryString = atob(base64)
  const bytes = new Uint8Array(binaryString.length)
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i)
  }
  return bytes
}

/**
 * Prints a PDF by opening it in a new browser tab, where the browser's
 * full PDF viewer correctly respects page dimensions and orientation.
 */
export async function printPdfNatively(
  signedUrl: string,
  preOpenedWindow?: Window | null
): Promise<void> {
  const response = await fetch(signedUrl)
  if (!response.ok) {
    throw new Error(`Failed to fetch PDF: ${response.status}`)
  }

  const blob = await response.blob()
  const blobUrl = URL.createObjectURL(blob)

  await printViaNewWindow(blobUrl, preOpenedWindow)
}

function printViaNewWindow(
  blobUrl: string,
  preOpenedWindow?: Window | null
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    let printWindow: Window

    if (preOpenedWindow && !preOpenedWindow.closed) {
      printWindow = preOpenedWindow
      printWindow.location.href = blobUrl
    } else {
      const opened = window.open(blobUrl, '_blank')
      if (!opened) {
        URL.revokeObjectURL(blobUrl)
        reject(new Error('Popup blocked — please allow popups for this site'))
        return
      }
      printWindow = opened
    }

    let resolved = false
    const resolveOnce = () => {
      if (resolved) return
      resolved = true
      resolve()
    }

    const cleanup = () => {
      clearInterval(pollClosed)
      URL.revokeObjectURL(blobUrl)
      if (!printWindow.closed) {
        printWindow.close()
      }
    }

    // Edge can fire afterprint before the user finishes with the dialog.
    // Delay cleanup to let the print pipeline complete.
    printWindow.addEventListener('afterprint', () => {
      setTimeout(() => {
        cleanup()
        resolveOnce()
      }, 500)
    })

    printWindow.onload = () => {
      printWindow.focus()
      printWindow.print()
    }

    const pollClosed = setInterval(() => {
      if (printWindow.closed) {
        cleanup()
        resolveOnce()
      }
    }, 500)
  })
}

function getBasePathFromConfig(): string {
  try {
    const el = document.getElementById('config-vars-embed')
    if (!el?.textContent) return ''
    const config = JSON.parse(el.textContent) as { BASE_PATH?: string }
    return typeof config.BASE_PATH === 'string' ? config.BASE_PATH : ''
  } catch {
    return ''
  }
}

function getPdfWorkerSrc(): string {
  const base = typeof document !== 'undefined' ? getBasePathFromConfig() : ''
  const path = base ? `/${base}/pdf.worker.min.js` : '/pdf.worker.min.js'
  return `${window.location.origin}${path}`
}

/**
 * Rasterizes document pages to canvas via pdfjs, builds a standalone
 * HTML document with CSS named pages for per-page orientation, and
 * prints it in a new window. Using a separate window ensures the @page
 * rules aren't overridden by the app's styles and removes Chrome's
 * default headers/footers via margin: 0.
 */
export async function printRasterized(
  signedUrl: string,
  preOpenedWindow?: Window | null
): Promise<void> {
  const pages = await rasterizePages(signedUrl)

  const imageHtml = pages
    .map(({ dataUrl, widthPt, heightPt }) => {
      const cls = widthPt > heightPt ? 'landscape' : 'portrait'
      return `<div class="page ${cls}"><img src="${dataUrl}"></div>`
    })
    .join('\n')

  await printHtml(buildPrintHtml(imageHtml), preOpenedWindow)
}

async function rasterizePages(signedUrl: string): Promise<RasterizedPage[]> {
  const pdfjsLib = await import('pdfjs-dist')

  if (!pdfjsLib.GlobalWorkerOptions.workerSrc) {
    pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerSrc()
  }

  const loadingTask = pdfjsLib.getDocument({ url: signedUrl, verbosity: 0 })
  const pdf = await loadingTask.promise

  const pages: RasterizedPage[] = []

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i)
    const pointsViewport = page.getViewport({ scale: 1 })
    const renderViewport = page.getViewport({ scale: 2 })

    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) {
      throw new Error(`Could not get canvas 2d context for page ${i}`)
    }
    canvas.width = renderViewport.width
    canvas.height = renderViewport.height

    await page.render({
      canvasContext: context,
      viewport: renderViewport,
      canvas,
    }).promise

    pages.push({
      dataUrl: canvas.toDataURL('image/png'),
      widthPt: pointsViewport.width,
      heightPt: pointsViewport.height,
    })
  }

  return pages
}

async function printHtml(
  html: string,
  preOpenedWindow?: Window | null
): Promise<void> {
  const blob = new Blob([html], { type: 'text/html' })
  const blobUrl = URL.createObjectURL(blob)
  await printViaNewWindow(blobUrl, preOpenedWindow)
}

function buildPrintHtml(body: string): string {
  return `<!DOCTYPE html>
<html>
<head>
<style>
  @page portPage { size: portrait; margin: 0; }
  @page landPage { size: landscape; margin: 0; }
  body { margin: 0; padding: 0; }
  .page { break-after: page; break-inside: avoid; position: relative; overflow: hidden; }
  .page:last-of-type { break-after: auto; }
  .page img { display: block; width: 100%; height: auto; }
  .portrait { page: portPage; }
  .landscape { page: landPage; }
</style>
</head>
<body>
${body}
</body>
</html>`
}
